import Modal, { ModalFooter } from "@amzn/meridian/modal";
import React, { useState, useEffect } from "react";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Input from "@amzn/meridian/input";
import Text from "@amzn/meridian/text";
import { Space } from "../../../space/Space";
import Box from "@amzn/meridian/box";
import { UpdateAppealRequest, useUpdateAppealMutation } from "../../../../endpoints/updateAppeal";
import { useAppSelector } from "../../../../app/store/hooks";
import {
    EditSalientFactsProps,
    SalientFactsData,
    FieldConfig,
    DEFAULT_SALIENT_FACTS,
} from "./types";
import { salientFactsConfig } from "./formConfig";

export const EditSalientFacts: React.FC<EditSalientFactsProps> = ({
    open,
    onClose,
    immutableInitialData,
}) => {
    const [salientFactsData, setSalientFactsData] = useState<SalientFactsData>(() => {
        const initialFacts =
            immutableInitialData?.appealDetails?.appealApproachCalculationData?.exportAppealsPackage
                ?.salientFacts;

        return {
            ...DEFAULT_SALIENT_FACTS,
            ...initialFacts,
            buildingArea: {
                total: initialFacts?.buildingArea?.total || "",
            },
            siteArea: {
                sqFt: initialFacts?.siteArea?.sqFt || "",
            },
        };
    });

    const [updateAppeal, { isLoading }] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);

    useEffect(() => {
        if (
            immutableInitialData?.appealDetails?.appealApproachCalculationData?.exportAppealsPackage
                ?.salientFacts
        ) {
            setSalientFactsData(
                immutableInitialData.appealDetails.appealApproachCalculationData
                    .exportAppealsPackage.salientFacts
            );
        }
    }, [immutableInitialData]);

    const handleInputChange = (field: string, value: string) => {
        setSalientFactsData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNestedInputChange = (
        parent: "buildingArea" | "siteArea",
        field: string,
        value: string
    ) => {
        setSalientFactsData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [field]: value,
            },
        }));
    };

    const handleSave = async () => {
        try {
            const updateData: UpdateAppealRequest = {
                businessUseCase: "SMART",
                appealArtifactId: immutableInitialData.appealArtifactId,
                appealDetails: {
                    ...immutableInitialData.appealDetails,
                    appealApproachCalculationData: {
                        ...immutableInitialData.appealDetails.appealApproachCalculationData,
                        exportAppealsPackage: {
                            ...immutableInitialData.appealDetails.appealApproachCalculationData
                                ?.exportAppealsPackage,
                            salientFacts: salientFactsData,
                        },
                    },
                },
                appealStatus: immutableInitialData.appealDetails.status,
                clientName: alias,
            };

            const result = await updateAppeal(updateData).unwrap();
            if (result.status === "SUCCESS") {
                onClose();
            } else {
                console.error("Failed to update appeal");
            }
        } catch (error) {
            console.error("Error updating appeal:", error);
        } finally {
            onClose();
        }
    };
    const renderInputField = (fieldConfig: FieldConfig) => {
        const value = fieldConfig.nested
            ? salientFactsData[fieldConfig.nested.parent][fieldConfig.nested.field]
            : salientFactsData[fieldConfig.field];

        const handleChange = (value: string) => {
            if (fieldConfig.nested) {
                handleNestedInputChange(fieldConfig.nested.parent, fieldConfig.nested.field, value);
            } else {
                handleInputChange(fieldConfig.field, value);
            }
        };

        return (
            <Box width="45%">
                <Text>{fieldConfig.label}</Text>
                <Input
                    value={value}
                    onChange={handleChange}
                    placeholder={fieldConfig.placeholder}
                />
            </Box>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Edit Salient Facts"
            width="30vw"
            aria-describedby="EditSalientFactsModal"
        >
            {salientFactsConfig.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                    <Row alignmentHorizontal="justify">
                        {row.map((field, fieldIndex) => (
                            <React.Fragment key={fieldIndex}>
                                {renderInputField(field)}
                            </React.Fragment>
                        ))}
                    </Row>
                    <Space size="s" />
                </React.Fragment>
            ))}

            <ModalFooter>
                <Row alignmentHorizontal="justify">
                    <Button type="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isLoading}>
                        {isLoading ? "Saving..." : "Save"}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
